import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Center, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { format, roundToNearestMinutes } from "date-fns";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MessageBoxButton from "../components/helpers/MessageBoxButton";
import { formatMobile, formatAUDate, formatAUTime } from "../global-client";
import {
  cancelMessageSchedule,
  getScheduledMessages,
  rescheduleMessage,
  sendScheduledMessageNow,
} from "../redux/actions/messageActions";
import ScheduledMessageRow from "./screenComponents/ScheduledMessageRow";

const ScheduledMessagesScreen = () => {
  const dispatch = useDispatch();
  const { loading, error, scheduled } = useSelector((state) => state.message);
  const [selectedRow, setSelectedRow] = useState(null);
  const [smsScheduleTime, setSmsScheduleTime] = useState(
    roundToNearestMinutes(new Date(), { roundingMethod: "ceil", nearestTo: 30 })
  ); // Default to next closest 30 minutes

  useEffect(() => {
    dispatch(getScheduledMessages());

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        dispatch(getScheduledMessages());
      }
    }, 10000); // refresh every 10 seconds

    return () => clearInterval(interval);
  }, [dispatch]);

  const selectMsgRow = (row, msg) => {
    //console.log("Selected message row: ", row);
    //console.log("Selected message data: ", msg);
    //setSmsScheduleTime(roundToNearestMinutes(new Date(), { roundingMethod: "ceil", nearestTo: 30 }));
    setSmsScheduleTime(msg.scheduleDateTime ? new Date(msg.scheduleDateTime) : roundToNearestMinutes(new Date(), { roundingMethod: "ceil", nearestTo: 30 }) );
    setSelectedRow(row);
  };

  return (
    <>
      {loading && <Center>Loading scheduled messages...</Center>}
      {!loading && scheduled && scheduled.length === 0 && (
        <Center p={4}>
          <Box textAlign="center">
            <Text fontSize="lg" color="gray.500">No Scheduled Messages</Text>
          </Box>
        </Center>
      )}
      {scheduled && scheduled.length >= 1 && (
        <Box p={4}>
          <Wrap spacing='5px' justify='center'>
            {error ? (
              <Alert status='error'>
                <AlertIcon />
                <AlertTitle>We are sorry </AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : (
              <Box width='100%' p='0' mt='8'>
                <ScheduledMessageRow
                  key={"header"}
                  id='ID'
                  text='Text'
                  toNumber='To Number'
                  fromNumber='From Number'
                  customerName='Customer Name'
                  createdOn='Created On'
                  scheduledFor='Scheduled For'
                  index={-1}
                  selected={false}
                  header={true}
                  // onSelect={(row) => {
                  //   selectMsgRow(row);
                  // }}
                  mt='8'
                />
                {scheduled.map((msg, index) => (
                  <ScheduledMessageRow
                    key={msg._id}
                    id={msg._id.slice(-5)}
                    text={msg.body}
                    toNumber={formatMobile(msg.to)}
                    fromNumber={formatMobile(msg.from)}
                    customerName={msg.smsData && msg.smsData.custName ? msg.smsData.custName : "N/A"}
                    createdOn={
                      msg.createdAt &&
                      formatAUDate(msg.createdAt, { weekday: false }) + ", " + formatAUTime(msg.createdAt)
                    }
                    scheduledFor={
                      msg.scheduleDateTime &&
                      formatAUDate(msg.scheduleDateTime, { weekday: false }) + ", " + formatAUTime(msg.scheduleDateTime)
                    }
                    index={index}
                    selected={selectedRow === index}
                    header={false}
                    onSelect={(row) => {
                      selectMsgRow(row, msg);
                    }}
                  />
                ))}
              </Box>
            )}
          </Wrap>
        </Box>
      )}
      {selectedRow !== null && scheduled && scheduled[selectedRow] && (
        <Center mt={8}>
          <Box p={6} borderWidth={1} borderRadius='md' boxShadow='md' minWidth='350px'>
            <form
              onSubmit={(e) => {
                e.preventDefault();

                // Example: dispatch(updateScheduledMessage(scheduled[selectedRow]._id, newDateTime));
                // Selected index: {selectedRow}
              }}
            >
              <Alert status='info' mb={4}>
                <AlertIcon />
                <AlertTitle mr={2}>Edit Scheduled Message #{selectedRow + 1}</AlertTitle>
              </Alert>
              <Box mb={4}>
                <label htmlFor='scheduledDateTime'>New Scheduled Date & Time: </label>
                <input
                  type='datetime-local'
                  id='scheduledDateTime'
                  name='scheduledDateTime'
                  style={{ marginLeft: "10px", padding: "6px", borderRadius: "4px", border: "1px solid #ccc" }}
                  value={format(smsScheduleTime, "yyyy-MM-dd'T'HH:mm")}
                  onChange={(e) => setSmsScheduleTime(new Date(e.target.value))}
                />
              </Box>
              <Wrap spacing={4}>
                <WrapItem>
                  <MessageBoxButton
                    buttonText='Update Schedule'
                    title='Update Scheduled Message'
                    description='Are you sure you want to update the schedule for this message?'
                    confirmText='Yes, Update it'
                    cancelText='Give up'
                    colorScheme='blue'
                    onConfirm={() => {
                      // Implement reschedule logic here
                      var smsData = { ...scheduled[selectedRow].smsData };
                      smsData.scheduledTime = smsScheduleTime;
                      // Implement update scheduledDateTime logic here
                      dispatch(rescheduleMessage(scheduled[selectedRow]._id, smsData));
                      setSelectedRow(null);
                    }}
                    buttonProps={{
                      type: "submit",
                      colorScheme: "blue",
                    }}
                  />
                </WrapItem>
                <WrapItem>
                  <MessageBoxButton
                    buttonText='Send Now'
                    title='Send Scheduled Message Now'
                    description='Are you sure you want to send this scheduled message now?'
                    confirmText='Yes, Send it'
                    cancelText='Give up'
                    colorScheme='green'
                    onConfirm={() => {
                      // Implement send now logic here
                      dispatch(sendScheduledMessageNow(scheduled[selectedRow]._id, scheduled[selectedRow].smsData));
                      setSelectedRow(null);
                    }}
                    buttonProps={{
                      colorScheme: "green",
                    }}
                  />
                </WrapItem>
                <WrapItem>
                  <MessageBoxButton
                    buttonText='Cancel Schedule'
                    title='Cancel Scheduled Message'
                    description='Are you sure you want to cancel this scheduled message? This action cannot be undone.'
                    confirmText='Yes, Cancel it'
                    cancelText='Give up'
                    colorScheme='red'
                    onConfirm={() => {
                      // Implement cancel scheduled message logic here
                      dispatch(cancelMessageSchedule(scheduled[selectedRow]._id));
                      setSelectedRow(null);
                    }}
                    buttonProps={{
                      colorScheme: "red",
                    }}
                  />
                </WrapItem>

                <WrapItem>
                  <Button variant='outline' onClick={() => setSelectedRow(null)}>
                    Close
                  </Button>
                </WrapItem>
              </Wrap>
            </form>
          </Box>
        </Center>
      )}
    </>
  );
};

export default ScheduledMessagesScreen;
